import { VStack, Box, HStack, Flex } from '@cad-arc';
import { InlineBanners } from '../components/inline-banners';
import CreateNarrativeForm from '../components/create-narrative-form';
import AdditionalInfoRenderer from '../additional-info-renderer/additional-info-renderer';
import React, { CSSProperties, useCallback, useRef, useState } from 'react';
import { AdditionalInfoRow } from '../components/additional-info-row';
import { UnreadButton } from '../components/unread-button';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import styles from '../components/narrative-panel.module.css';
import { EditorControl } from '../../common/components/lexical';
import { OnLexicalError } from '../../common/components/lexical/types';
import {
    AdditionalInfoNotificationManager,
    SortDirection,
    UnreadInfo,
} from '../hooks/use-additional-infos/types';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import { NarrativeJumpButtons } from '../components/narrative-jump-buttons';
import { NarrativeSortDirectionButton } from '../components/narrative-sort-direction-button';
import { FR_JUMP_BUTTONS_RESERVED_WIDTH } from '../constants';
import { useTogglePinnedMessagesShortcut } from '../hooks/use-toggle-pinned-messages-shortcut/use-toggle-pinned-messages-shortcut';

export interface BoloPanelAdditionalInfosProps {
    boloId: number;
    boloNumber: string; // used to show agency event number in the error alert
    currentUserId: number;
    firstUnreadInfo?: UnreadInfo; // this is used by additional infos to determine where to show the unread line initially
    focusInputRef?: React.MutableRefObject<(() => void) | undefined>; // this is used by parent components to focus the narrative input
    isPanelFocused?: boolean; // this is used to determine if the unread infos in view should be marked read
    isFirstResponder?: boolean; // this does specific configurations and changes for First Responder
    isReadOnlyMode?: boolean; // this is for a First Responder 'Narrative' tab view
    onAllRead?: (id: number) => void; // this contains a function which will be executed to let the parent know that all unreads have been seen
    onLexicalError?: OnLexicalError; // this is triggered when the lexical component throws an error, and should be used by parent to emit the error to monitoring
    notificationManager?: AdditionalInfoNotificationManager; // Adds compatibility for controlling event notifications from different sources.
    forceReload?: boolean;
}

export const BoloPanelAdditionalInfos = ({
    currentUserId,
    isPanelFocused,
    isFirstResponder = false,
    isReadOnlyMode,
    onAllRead,
    forceReload,
    onLexicalError,
}: BoloPanelAdditionalInfosProps) => {
    const editorControlRef = useRef<EditorControl>();
    useTogglePinnedMessagesShortcut(isFirstResponder);
    const canCreateAdditionalInfos = useEnabledFeatures()?.['createAdditionalInfos'] ?? true;
    const canDeleteAdditionalInfos = useEnabledFeatures()?.['deleteAdditionalInfos'] ?? true;
    const narrativeFilteringEnabled =
        useEnabledFeatures()?.rollOutCadNarrativeFilteringAndFocusImprovementsEnabled ?? false;
    const { sortDirection } = useAdditionalInfos();
    const isAscLayout = sortDirection === SortDirection.ASC;
    const showDirectionToggle = narrativeFilteringEnabled;
    const showNarrativeForm = !isReadOnlyMode && canCreateAdditionalInfos;
    const showJumpButtons = narrativeFilteringEnabled;
    const jumpHandlersRef = useRef<{
        jumpToNewest: () => void;
        jumpToOldest: () => void;
    } | null>(null);
    const handleJumpToNewest = useCallback(() => jumpHandlersRef.current?.jumpToNewest(), []);
    const handleJumpToOldest = useCallback(() => jumpHandlersRef.current?.jumpToOldest(), []);
    const onScrollToTop = isAscLayout ? handleJumpToOldest : handleJumpToNewest;
    const onScrollToBottom = isAscLayout ? handleJumpToNewest : handleJumpToOldest;
    const [jumpButtonEdgeState, setJumpButtonEdgeState] = useState({
        isAtTop: false,
        isAtBottom: false,
        isScrollable: false,
    });
    const additionalInfoRendererProps = {
        rowComponent: AdditionalInfoRow,
        addonComponent: UnreadButton,
        isBolo: true,
        currentUserId,
        markVisibleAsRead: isPanelFocused,
        canDeleteInfos: canDeleteAdditionalInfos && !isReadOnlyMode,
        onAllRead,
        forceReload,
        isReadOnlyMode,
        isFirstResponder,
        jumpHandlersRef,
        onEdgeStateChange: setJumpButtonEdgeState,
        hasInlineJumpButtons: showJumpButtons && !isFirstResponder,
    };

    return (
        <VStack
            align="stretch"
            height="100%"
            width="100%"
            borderLeft="sm"
            borderColor="border.default"
            bg="surface.canvas"
        >
            <InlineBanners />
            {showDirectionToggle && (
                <Box paddingInlineEnd="4" paddingTop="2">
                    <HStack justify="end">
                        <NarrativeSortDirectionButton />
                    </HStack>
                </Box>
            )}
            {showNarrativeForm && (
                <Box
                    order={isAscLayout ? 2 : undefined}
                    paddingInlineStart={isAscLayout ? undefined : '4'}
                    paddingInlineEnd={isAscLayout ? undefined : '4'}
                    paddingTop={isAscLayout ? undefined : '2'}
                    className={isAscLayout ? styles['narrative-form-bottom'] : undefined}
                >
                    <CreateNarrativeForm
                        editorControlRef={editorControlRef}
                        onLexicalError={onLexicalError}
                    />
                </Box>
            )}
            {!isFirstResponder && showJumpButtons && (
                <Flex
                    justify="flex-end"
                    order={isAscLayout ? 1 : undefined}
                    paddingInlineEnd="var(--arc-space-2)"
                >
                    <NarrativeJumpButtons
                        onScrollToTop={onScrollToTop}
                        onScrollToBottom={onScrollToBottom}
                        isAtTop={jumpButtonEdgeState.isAtTop}
                        isAtBottom={jumpButtonEdgeState.isAtBottom}
                        isDisabled={!jumpButtonEdgeState.isScrollable}
                        isAscLayout={isAscLayout}
                    />
                </Flex>
            )}
            {isFirstResponder ? (
                <Box
                    position="relative"
                    flex={1}
                    minHeight={0}
                    style={
                        showJumpButtons
                            ? ({
                                  '--jump-buttons-overlay-width': FR_JUMP_BUTTONS_RESERVED_WIDTH,
                              } as CSSProperties)
                            : undefined
                    }
                >
                    <Box height="100%" paddingTop={isReadOnlyMode ? '2' : undefined}>
                        <AdditionalInfoRenderer {...additionalInfoRendererProps} />
                    </Box>
                    {showJumpButtons && (
                        <Flex
                            direction="column"
                            justify={isAscLayout ? 'flex-end' : 'flex-start'}
                            position="absolute"
                            top={0}
                            bottom={0}
                            right="var(--scrollbar-width)"
                            pointerEvents="none"
                        >
                            <Box pointerEvents="auto">
                                <NarrativeJumpButtons
                                    onScrollToTop={onScrollToTop}
                                    onScrollToBottom={onScrollToBottom}
                                    isAtTop={jumpButtonEdgeState.isAtTop}
                                    isAtBottom={jumpButtonEdgeState.isAtBottom}
                                    isDisabled={!jumpButtonEdgeState.isScrollable}
                                    isAscLayout={isAscLayout}
                                    isFirstResponder
                                />
                            </Box>
                        </Flex>
                    )}
                </Box>
            ) : (
                <AdditionalInfoRenderer {...additionalInfoRendererProps} />
            )}
        </VStack>
    );
};
